import { Formik } from 'formik';
import { FC } from 'react';
import * as yup from 'yup';
import { useFormContext } from '../../../../../contexts/FormContext';
import {
  AudienceRetargetInput,
  FormType,
} from '../../../../../types/FormTypes';
import { ListenToFormikValues } from '../../../../shared/formik/ListenToFormikValues';
import { GenerateResponseButtons } from '../GenerateResponseButtons';

import formStyles from '../formStyles.module.css';
import { OriginalCopyTextarea } from '../formInputs/OriginalCopyTextarea';
import { TargetDemographicInputs } from '../formInputs/TargetDemographicInputs';

const AudienceRetargetSchema = yup.object<
  Record<keyof AudienceRetargetInput, yup.AnySchema>
>({
  originalCopy: yup
    .string()
    .required('Please paste the copy that you would like to improve'),
  targetDemographic: yup
    .array()
    .of(yup.string())
    .min(1, 'Please select at least 1 target demographic'),
  prevResponse: yup.string().notRequired(),
  advancedInputs: yup.boolean().notRequired(),
});

export const AudienceRetargetForm: FC = () => {
  const {
    audienceRetargetInput,
    setAudienceRetargetInput,
    setCanKeepGoing,
    loading,
    submitToolkitForm,
  } = useFormContext();

  return (
    <Formik
      initialValues={audienceRetargetInput}
      onSubmit={async (values, actions) => {
        await submitToolkitForm({
          values,
          actions,
          formType: FormType.AUDIENCE_RETARGET,
        });
      }}
      validationSchema={AudienceRetargetSchema}
    >
      {(props) => (
        <>
          <div className={formStyles['form-container']}>
            <ListenToFormikValues
              callBackFunc={(values) => {
                setAudienceRetargetInput(values as AudienceRetargetInput);
                setCanKeepGoing(false);
              }}
            />
            <div>
              <OriginalCopyTextarea
                formikProps={props}
                disabled={loading}
                title="What copy do you want to retarget?"
                subtitle="Please provide the copy that you want to retarget for a different demographic."
                placeholder="Copy that needs improvement"
              />
            </div>
            <div>
              <TargetDemographicInputs
                formikProps={props}
                disabled={loading}
                title="Who is the new targeted demographic?"
                subtitle="Describe your targeted demographic (Age, gender, occupation, etc). Select all that apply."
              />
            </div>
          </div>
          <GenerateResponseButtons
            onSubmit={() => {
              props.handleSubmit();
            }}
          />
        </>
      )}
    </Formik>
  );
};
